import { formatINR } from "@templeos/shared";
import { Button, EmptyState, Modal, MoneyField, SelectField, StatCard, useConfirm, useToast } from "@templeos/ui";
import { useCallback, useMemo, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { BillPanel } from "../components/BillPanel.js";
import { useBill } from "../components/checkout.js";
import { Shell } from "../components/Shell.js";
import { CashAlert, ShiftDrawer, useShiftTally } from "../components/ShiftPanel.js";
import { ReprintModal } from "../components/SupervisorActions.js";
import { addDonation, addItem, CartError, istMinutes, pickSlot, type Cart } from "../lib/cart.js";
import { openCustomerDisplay } from "../lib/display.js";
import { pendingByItem } from "../lib/queue.js";
import { quotaAlert, quotaBreach, quotaLeft } from "../lib/rules.js";
import { SELL_KEYS, type SellAction } from "../lib/shortcuts.js";
import type { PosItem, QueuedSale } from "../lib/types.js";
import { useSession } from "../session.js";

const KIND_LABEL: Record<string, string> = { ticket: "Darshan", seva: "Seva & pooja", prasad: "Prasad", other: "Other" };
const groupOf = (i: PosItem): string => i.category ?? KIND_LABEL[i.kind] ?? "Other";

export function ItemTile({ item, onAdd, disabledReason, badge, pressed }: { item: PosItem; onAdd: () => void; disabledReason?: string | null; badge?: string; pressed?: boolean }) {
  const img = typeof item.meta.imageUrl === "string" && /^(\/|https?:\/\/)/.test(item.meta.imageUrl) ? item.meta.imageUrl : null;
  return (
    <button type="button" className="pos-tile" style={{ "--tile": item.color ?? undefined } as CSSProperties} aria-disabled={disabledReason ? true : undefined}
      aria-pressed={pressed} aria-label={`${item.name}, ${formatINR(item.price)}${disabledReason ? `, ${disabledReason}` : ""}`} onClick={() => { if (!disabledReason) onAdd(); }}>
      {img && <img className="pos-tile-img" src={img} alt="" loading="lazy" />}
      <span>
        <span className="pos-tile-name">{item.name}</span>
        {item.nameHi && <span className="pos-tile-local" lang="hi"><br />{item.nameHi}</span>}
      </span>
      <span className="pos-tile-meta">
        <span className="pos-tile-price">{item.price ? formatINR(item.price, { decimals: false }) : "Free"}</span>
        <span className="tos-muted">{disabledReason ?? badge ?? item.code}</span>
      </span>
    </button>
  );
}

/** SOW Module 1: offline-first ticket billing with category tiles, scanner input, split payment and F1–F12 keys. */
export function Sell() {
  const s = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { sales, t, drawer, quotaUsed } = useShiftTally();
  const items = useMemo(() => s.config?.items ?? [], [s.config]);
  const pending = useMemo(() => pendingByItem(sales), [sales]);
  const cashierQuota = s.config?.cashierQuota ?? null;
  const validate = useCallback((cart: Cart) => quotaBreach(cart, items, pending, cashierQuota, quotaUsed), [items, pending, cashierQuota, quotaUsed]);
  const bill = useBill(`tos-pos-cart:${s.me?.counter?.code ?? ""}:${s.me?.username ?? ""}:sell`, s.config?.items ?? null, validate);
  const [group, setGroup] = useState<string>("All");
  const [search, setSearch] = useState("");
  const [donate, setDonate] = useState<{ fund: string; amount: number | null; error?: { fund?: string; amount?: string } } | null>(null);
  const [reprint, setReprint] = useState<{ receiptId: string; local?: QueuedSale } | null>(null);
  const [shiftOpen, setShiftOpen] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);

  const groups = useMemo(() => ["All", ...new Set(items.map(groupOf)), ...(s.config?.funds.length ? ["Donation"] : [])], [items, s.config]);
  const q = search.trim().toLowerCase();
  const shown = items.filter((i) => (group === "All" || groupOf(i) === group) && (!q || i.name.toLowerCase().includes(q) || i.code.toLowerCase().includes(q) || (i.nameHi ?? "").includes(search.trim())));
  const nowMin = istMinutes();
  const donateFund = s.config?.funds.find((f) => f.code === donate?.fund);

  const add = (item: PosItem, qty = 1) => {
    try {
      bill.setCart(addItem(bill.cart, item, qty));
    } catch (e) {
      if (e instanceof CartError) toast(e.message, "danger"); else throw e;
    }
  };

  const onScan = () => {
    const code = search.trim().toUpperCase();
    if (!code) return;
    const exact = items.find((i) => i.code.toUpperCase() === code);
    const hit = exact ?? (shown.length === 1 ? shown[0] : undefined);
    if (hit) { add(hit); setSearch(""); } else toast(`No item matches "${search.trim()}".`, "danger");
  };

  const lastSynced = [...sales].reverse().find((x) => x.receiptId && !x.voided) ?? null;
  const onKey = async (a: SellAction) => {
    switch (a) {
      case "search": scanRef.current?.focus(); break;
      case "payCash": bill.pay.setChoice("cash"); break;
      case "payUpi": bill.pay.setChoice("upi"); break;
      case "paySplit": bill.pay.setChoice("split"); break;
      case "checkout": void bill.submit(); break;
      case "clearCart":
        if (bill.cart.length && (await confirm({ title: "Clear this bill?", message: "All lines in the current bill will be removed.", confirmLabel: "Clear bill", danger: true }))) bill.setCart([]);
        break;
      case "reprintLast":
        if (lastSynced?.receiptId) setReprint({ receiptId: lastSynced.receiptId, local: lastSynced });
        else toast(sales.length ? "The last bill has not reached the server yet; reprint after it syncs." : "No bill in this shift yet.", "danger");
        break;
      case "history": navigate("/history"); break;
      case "shiftSummary": setShiftOpen(true); break;
      case "fastMode": navigate("/fast"); break;
      case "customerDisplay": openCustomerDisplay(); break;
      case "lock": s.lock(); break;
    }
  };

  const saveDonation = (e: FormEvent) => {
    e.preventDefault();
    if (!donate) return;
    if (!donateFund) return setDonate({ ...donate, error: { fund: "Choose a fund." } });
    if (donate.amount === null) return setDonate({ ...donate, error: { amount: "Enter the amount." } });
    try {
      bill.setCart(addDonation(bill.cart, donateFund, donate.amount));
      setDonate(null);
    } catch (err) {
      if (err instanceof CartError) setDonate({ ...donate, error: { amount: err.message } }); else throw err;
    }
  };

  return (
    <Shell keys={SELL_KEYS} onKey={(a) => void onKey(a)}>
      <div className="pos-sell">
        <section className="pos-left" aria-label="Items">
          <div className="pos-tally">
            <StatCard label="Bills this shift" value={t.bills} />
            <StatCard label="Tickets issued" value={t.tickets} tone="accent" />
            <StatCard label="Collected" value={formatINR(t.amount, { decimals: false })} tone="success" />
            <StatCard label="Waiting to sync" value={t.pending} tone={t.pending ? "danger" : undefined} />
          </div>
          <CashAlert drawer={drawer} />
          <div className="tos-field">
            <label className="tos-label" htmlFor="pos-scan">Scan barcode or search (F1)</label>
            <input id="pos-scan" ref={scanRef} className="tos-input" autoComplete="off" placeholder="Item code, name or scan…" value={search}
              onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onScan(); } }} />
          </div>
          <div className="pos-cats" role="group" aria-label="Categories">
            {groups.map((g) => (
              <button key={g} type="button" className="pos-cat" aria-pressed={group === g}
                onClick={() => (g === "Donation" ? setDonate({ fund: s.config?.funds[0]?.code ?? "", amount: null }) : setGroup(g))}>{g}</button>
            ))}
          </div>
          {!s.config ? (
            <EmptyState title="Counter items not loaded" action={<Button onClick={() => void s.reloadConfig()}>Reload counter items</Button>}>
              Check the connection, then reload the counter configuration.
            </EmptyState>
          )
            : !shown.length ? <EmptyState title="No items">Nothing matches this category or search.</EmptyState>
              : (
                <div className="pos-tiles">
                  {shown.map((i) => {
                    const closed = i.requiresDate && i.slots.length > 0 && !pickSlot(i.slots, nowMin) ? "Closed for today" : null;
                    const soldOut = quotaLeft(i, pending.get(i.id) ?? 0) <= 0 ? "Today's quota is full" : null;
                    const slot = i.requiresDate && i.slots.length ? pickSlot(i.slots, nowMin) : null;
                    return <ItemTile key={i.id} item={i} onAdd={() => add(i)} disabledReason={closed ?? soldOut} badge={quotaAlert(i, pending.get(i.id) ?? 0) ?? slot ?? undefined} />;
                  })}
                </div>
              )}
        </section>
        <BillPanel bill={bill} />
      </div>

      <Modal open={Boolean(donate)} title="Add a donation" onClose={() => setDonate(null)}
        footer={<><Button onClick={() => setDonate(null)}>Cancel</Button><Button type="submit" form="pos-donation-form" variant="primary">Add to bill</Button></>}>
        {donate && (
          <form id="pos-donation-form" className="tos-stack" onSubmit={saveDonation} noValidate>
            <SelectField label="Fund" required value={donate.fund} error={donate.error?.fund} onChange={(e) => setDonate({ ...donate, fund: e.target.value, error: undefined })}
              options={(s.config?.funds ?? []).map((f) => ({ value: f.code, label: `${f.name}${f.eligible80g ? " (80G)" : ""}` }))} />
            <MoneyField label="Amount (₹)" required value={donate.amount} error={donate.error?.amount} onChange={(amount) => setDonate({ ...donate, amount, error: undefined })}
              hint={donateFund?.minAmount ? `Minimum ${formatINR(donateFund.minAmount)}` : undefined} />
          </form>
        )}
      </Modal>
      <ReprintModal target={reprint} onClose={() => setReprint(null)} />
      <ShiftDrawer open={shiftOpen} onClose={() => setShiftOpen(false)} />
    </Shell>
  );
}
